import React, { useEffect, useState } from "react";
import { Banknote, Check, X as XIcon, Settings, RefreshCcw, Mail } from "lucide-react";
import { toast } from "sonner";
import AdminLayout from "../../components/AdminLayout";
import { api } from "../../lib/api";

const STATUS_TABS = [
  { id: "all", label: "All" },
  { id: "pending", label: "Pending" },
  { id: "approved", label: "Approved" },
  { id: "paid_out", label: "Paid Out" },
  { id: "cancelled", label: "Cancelled" },
];

const STATUS_STYLES = {
  pending: "bg-yellow-500/15 text-yellow-400 border-yellow-500/30",
  approved: "bg-violet-500/15 text-violet-300 border-violet-500/30",
  paid_out: "bg-[#CFF048]/15 text-[#CFF048] border-[#CFF048]/40",
  cancelled: "bg-red-500/15 text-red-400 border-red-500/30",
};

export default function AdminAffiliates() {
  const [tab, setTab] = useState("pending");
  const [earnings, setEarnings] = useState([]);
  const [affiliates, setAffiliates] = useState([]);
  const [stats, setStats] = useState({ pending: 0, approved: 0, paid_out: 0, cancelled: 0, total_referrers: 0, total_orders: 0, settings: { commission_per_unit: 100 } });
  const [loading, setLoading] = useState(true);
  const [commission, setCommission] = useState(100);
  const [savingCommission, setSavingCommission] = useState(false);
  const [actingId, setActingId] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const [e, a, s] = await Promise.all([
        api.get(`/admin/affiliates/earnings?status=${tab}`),
        api.get("/admin/affiliates"),
        api.get("/admin/affiliates/stats"),
      ]);
      setEarnings(e.data || []);
      setAffiliates(a.data || []);
      setStats(s.data || {});
      setCommission(s.data?.settings?.commission_per_unit || 100);
    } catch { toast.error("Failed to load"); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [tab]); // eslint-disable-line react-hooks/exhaustive-deps

  const updateStatus = async (id, status) => {
    setActingId(id);
    try {
      let payout_reference = null;
      if (status === "paid_out") {
        payout_reference = window.prompt("Payment reference (UPI txn ID / bank UTR — optional):") || "";
      }
      await api.put(`/admin/affiliates/earnings/${id}`, { status, payout_reference });
      toast.success(`MOVED TO ${status.replace("_", " ").toUpperCase()}`);
      load();
    } catch (err) {
      toast.error("Failed", { description: err?.response?.data?.detail || "" });
    } finally { setActingId(""); }
  };

  const saveCommission = async () => {
    setSavingCommission(true);
    try {
      await api.put("/admin/affiliates/settings", { commission_per_unit: parseInt(commission || 0, 10) });
      toast.success("COMMISSION UPDATED");
      load();
    } catch (err) {
      toast.error("Failed", { description: err?.response?.data?.detail || "" });
    } finally { setSavingCommission(false); }
  };

  return (
    <AdminLayout title="Cult Affiliates · Referral Program">
      {/* TOP STATS */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-6" data-testid="affiliates-stats">
        <div className="border border-[#27272A] bg-[#0A0A0A] p-5">
          <p className="label-mono text-[#71717A]">// PENDING</p>
          <p className="font-display font-black text-2xl text-yellow-400 mt-1">₹{stats.pending || 0}</p>
        </div>
        <div className="border border-[#27272A] bg-[#0A0A0A] p-5">
          <p className="label-mono text-[#71717A]">// APPROVED</p>
          <p className="font-display font-black text-2xl text-violet-300 mt-1">₹{stats.approved || 0}</p>
        </div>
        <div className="border border-[#27272A] bg-[#0A0A0A] p-5">
          <p className="label-mono text-[#71717A]">// PAID OUT</p>
          <p className="font-display font-black text-2xl text-[#CFF048] mt-1">₹{stats.paid_out || 0}</p>
        </div>
        <div className="border border-[#27272A] bg-[#0A0A0A] p-5">
          <p className="label-mono text-[#71717A]">// AFFILIATES · ORDERS</p>
          <p className="font-display font-black text-2xl mt-1">{stats.total_referrers || 0} <span className="text-[#71717A] text-base">·</span> {stats.total_orders || 0}</p>
        </div>
      </div>

      {/* COMMISSION SETTING */}
      <div className="border border-[#27272A] bg-[#0A0A0A] p-5 mb-6" data-testid="commission-settings">
        <div className="flex items-end justify-between gap-4 flex-wrap">
          <div>
            <p className="label-mono text-[#CFF048] flex items-center gap-2"><Settings size={12} /> // COMMISSION SETTINGS</p>
            <h3 className="font-display font-black text-xl mt-1">₹ per tee sold via referral</h3>
            <p className="label-mono text-[#71717A] mt-1">// Affects all FUTURE orders. Existing earnings keep their original rate.</p>
          </div>
          <div className="flex items-end gap-2">
            <div>
              <label className="label-mono text-[#71717A] block mb-1">AMOUNT (INR)</label>
              <input
                type="number"
                min="0"
                max="10000"
                value={commission}
                onChange={(e) => setCommission(e.target.value)}
                className="cult-input w-32"
                data-testid="commission-input"
              />
            </div>
            <button
              onClick={saveCommission}
              disabled={savingCommission}
              className="btn-neon disabled:opacity-50"
              data-testid="save-commission-btn"
            >
              {savingCommission ? "SAVING..." : "SAVE"}
            </button>
          </div>
        </div>
      </div>

      {/* LEADERBOARD */}
      {affiliates.length > 0 && (
        <div className="border border-[#27272A] bg-[#0A0A0A] mb-6" data-testid="affiliates-leaderboard">
          <div className="p-4 border-b border-[#27272A] flex items-center justify-between">
            <p className="font-display font-black">Top Affiliates</p>
            <button onClick={load} className="label-mono text-[#71717A] hover:text-[#CFF048] flex items-center gap-1" data-testid="affiliates-refresh">
              <RefreshCcw size={12} /> REFRESH
            </button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-black border-b border-[#27272A]">
                <tr className="label-mono text-[#71717A]">
                  <th className="text-left p-3">REFERRER</th>
                  <th className="text-left p-3">CODE</th>
                  <th className="text-left p-3">ORDERS</th>
                  <th className="text-left p-3">PENDING</th>
                  <th className="text-left p-3">APPROVED</th>
                  <th className="text-left p-3">PAID OUT</th>
                </tr>
              </thead>
              <tbody>
                {affiliates.map((a) => (
                  <tr key={a.user_id} className="border-b border-[#18181B]" data-testid={`affiliate-${a.user_id}`}>
                    <td className="p-3">
                      <p className="truncate">{a.name || "—"}</p>
                      <p className="label-mono text-[#71717A] truncate text-[10px]">{a.email}</p>
                    </td>
                    <td className="p-3 label-mono text-[#CFF048]">{a.ref_code}</td>
                    <td className="p-3">{a.orders}</td>
                    <td className="p-3 text-yellow-400">₹{a.pending}</td>
                    <td className="p-3 text-violet-300">₹{a.approved}</td>
                    <td className="p-3 text-[#CFF048] font-bold">₹{a.paid_out}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* TABS */}
      <div className="flex flex-wrap gap-2 mb-4 border-b border-[#27272A]" data-testid="affiliate-tabs">
        {STATUS_TABS.map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`px-4 py-2 label-mono uppercase transition-colors -mb-px border-b-2 ${
              tab === t.id ? "text-[#CFF048] border-[#CFF048]" : "text-[#71717A] border-transparent hover:text-white"
            }`}
            data-testid={`affiliate-tab-${t.id}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* EARNINGS TABLE */}
      {loading ? (
        <div className="text-center label-mono text-[#71717A] py-20">// LOADING...</div>
      ) : earnings.length === 0 ? (
        <div className="border border-[#27272A] p-12 text-center label-mono text-[#71717A]" data-testid="earnings-empty">
          // NO {tab.toUpperCase()} EARNINGS
        </div>
      ) : (
        <div className="border border-[#27272A] bg-[#0A0A0A] overflow-x-auto" data-testid="earnings-list">
          <table className="w-full text-sm">
            <thead className="bg-black border-b border-[#27272A]">
              <tr className="label-mono text-[#71717A]">
                <th className="text-left p-3">DATE</th>
                <th className="text-left p-3">REFERRER</th>
                <th className="text-left p-3">BUYER</th>
                <th className="text-left p-3">UNITS</th>
                <th className="text-left p-3">AMOUNT</th>
                <th className="text-left p-3">STATUS</th>
                <th className="text-left p-3">ACTIONS</th>
              </tr>
            </thead>
            <tbody>
              {earnings.map((e) => {
                const busy = actingId === e.id;
                return (
                  <tr key={e.id} className="border-b border-[#18181B] hover:bg-[#18181B]" data-testid={`admin-earning-${e.id}`}>
                    <td className="p-3 label-mono text-[#71717A] whitespace-nowrap">{new Date(e.created_at).toLocaleDateString()}</td>
                    <td className="p-3">
                      <p className="truncate">{e.referrer_name}</p>
                      <p className="label-mono text-[#71717A] truncate text-[10px] flex items-center gap-1">
                        <Mail size={10} /> {e.referrer_email}
                      </p>
                      <p className="label-mono text-[#CFF048] truncate text-[10px] mt-0.5">{e.ref_code}</p>
                    </td>
                    <td className="p-3">
                      <p className="truncate">{e.buyer_name}</p>
                      <p className="label-mono text-[#71717A] truncate text-[10px]">{e.buyer_email}</p>
                    </td>
                    <td className="p-3">{e.units} <span className="label-mono text-[#71717A]">× ₹{e.commission_per_unit}</span></td>
                    <td className="p-3 text-[#CFF048] font-bold">₹{e.amount}</td>
                    <td className="p-3">
                      <span className={`label-mono ${STATUS_STYLES[e.status] || STATUS_STYLES.pending} border px-2 py-0.5 text-[10px] uppercase`}>
                        {e.status.replace("_", " ")}
                      </span>
                      {e.payout_reference && (
                        <p className="label-mono text-[#71717A] text-[9px] mt-1 truncate max-w-[140px]">REF: {e.payout_reference}</p>
                      )}
                    </td>
                    <td className="p-3">
                      <div className="flex flex-wrap gap-1">
                        {e.status === "pending" && (
                          <button onClick={() => updateStatus(e.id, "approved")} disabled={busy} className="bg-violet-500/20 hover:bg-violet-500/30 text-violet-300 border border-violet-500/40 px-2 py-1 label-mono disabled:opacity-50" data-testid={`approve-${e.id}`}>
                            APPROVE
                          </button>
                        )}
                        {(e.status === "approved" || e.status === "pending") && (
                          <button onClick={() => updateStatus(e.id, "paid_out")} disabled={busy} className="bg-[#CFF048] hover:bg-white text-black px-2 py-1 label-mono flex items-center gap-1 disabled:opacity-50" data-testid={`payout-${e.id}`}>
                            <Banknote size={10} /> MARK PAID
                          </button>
                        )}
                        {e.status !== "cancelled" && (
                          <button onClick={() => updateStatus(e.id, "cancelled")} disabled={busy} className="border border-[#27272A] hover:border-red-500 hover:text-red-400 text-[#71717A] px-2 py-1 label-mono disabled:opacity-50" data-testid={`cancel-${e.id}`}>
                            <XIcon size={10} />
                          </button>
                        )}
                        {e.status === "cancelled" && (
                          <button onClick={() => updateStatus(e.id, "pending")} disabled={busy} className="border border-[#27272A] hover:border-[#CFF048] hover:text-[#CFF048] text-[#71717A] px-2 py-1 label-mono disabled:opacity-50" data-testid={`restore-${e.id}`}>
                            <Check size={10} /> RESTORE
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </AdminLayout>
  );
}
